// ============================================================
// แดชบอร์ดงานขาย (/sale-dashboard)
// ------------------------------------------------------------
// สรุปภาพรวมงานขายของโครงการที่เลือกอยู่
//
// ★ ทุกตัวเลขมาจาก GET api/admin/sale-dashboard ซึ่งนับจากข้อมูลจริง
//   ไม่มีค่าสมมติหรือค่าเปรียบเทียบย้อนหลัง เพราะระบบยังไม่เก็บประวัติรายวัน
//   ช่องไหนไม่มีข้อมูลรองรับก็ไม่มีในหน้านี้ ดีกว่าโชว์เลขที่เชื่อไม่ได้
//
// ใช้โครงการ์ดชุดเดียวกับหน้าจัดการทรัพย์ (prop-card) + ธีมสี CDS
// ============================================================

import React, { Component } from "react";
import PropertyNavbar from "../../components/PropertyNavbar.js";
import { loading, GET, getCurrentProject } from "../../components/CustomComponent.js";
import { stripJuristic } from "../../components/text-sanitize.js";
import {
    LISTING_TYPE,
    PUBLISH_STATUS,
    INQUIRY_STATUS,
    INQUIRY_STATUS_STYLE,
    INQUIRY_STATUS_TONE,
    money,
} from "../../constants/sale.js";

import "../../assets/css/bootstrap.css";
import "../../assets/css/style.css";
import "../../assets/css/property.css";
import "../../assets/css/property-theme-cds.css";
import "../../assets/css/sale-dashboard.css";

// ย่อจำนวนเงินให้อ่านเร็ว — ราคาบ้าน/คอนโดมีหลักล้านเสมอ เขียนเต็มแล้วตาลาย
const shortMoney = (v) => {
    const n = Number(v || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(2)} ล้าน`;
    if (n >= 1000) return `${Math.round(n / 1000)}K`;
    return money(n);
};

const share = (v, total) => (Number(total) > 0 ? Math.round((Number(v) / Number(total)) * 100) : 0);

// สีของแต่ละสถานะ ใช้ชุดเดียวกับที่หน้าจัดการทรัพย์ใช้
const TONE = {
    "ว่าง": "#198754",
    "จอง": "#f0921e",
    "ขายแล้ว": "#2f80ed",
    "ให้เช่าแล้ว": "#2f80ed",
    "ยกเลิก": "#adb5bd",
    "ไม่ระบุ": "#ced4da",
};

// ★ สีสถานะคำขอใช้ชุดกลาง (constants/sale.js) — "ปิดงาน" เทาเหมือนโมดัลนัดหมาย ไม่ใช่เขียว
const INQUIRY_TONE = INQUIRY_STATUS_TONE;

// "YYYY-MM-DD" -> "dd/mm" (แกนกราฟ) — ระบบแสดงวันที่แบบวัน/เดือนทุกหน้า
const dayMonth = (date) => {
    const [, month, day] = String(date || "").split("-");
    return day && month ? `${day}/${month}` : String(date || "");
};

const EMPTY = {
    portfolio: { room_total: 0, listing_total: 0, not_listed: 0, published: 0, hidden: 0, sell_with_tenant: 0 },
    selling_status: {},
    rent_status: {},
    listing_type: {},
    price: { avg: 0, min: 0, max: 0, views: 0 },
    inquiry: { total: 0, status: {}, visit_today: 0, daily: [] },
    top_viewed: [],
    recent_inquiries: [],
};

export default class SaleDashboard extends Component {
    state = { data: { ...EMPTY }, loaded: false };

    componentDidMount() {
        if (!localStorage.getItem("cds-acc-tk")) {
            localStorage.clear();
            window.location.href = "/";
            return;
        }
        loading(false);
        this.Get();
    }

    Get = async () => {
        loading(true);
        const result = await GET("api/admin/sale-dashboard");
        loading(false);
        if (result?.status) this.setState({ data: { ...EMPTY, ...result.data }, loaded: true });
        else this.setState({ loaded: true });
    };

    // ---------- ชิ้นส่วนที่ใช้ซ้ำ ----------

    section = (title, note) => (
        <div className="dash-section">
            <span className="dash-section__dot" />
            <span className="fw-bold text-dark text-nowrap">{title}</span>
            {!!note && <span className="text-12 text-secondary text-nowrap">{note}</span>}
            <div className="dash-section__rule" />
        </div>
    );

    chip = (icon, color, tint) => (
        <span className="dash-chip icon" style={{ background: tint, color }}>
            {icon}
        </span>
    );

    // การ์ดตัวเลขใบเดียว
    tile = ({ label, value, unit, icon, color = "#198754", tint = "#e8f5ef", foot }) => (
        <div className="prop-card prop-in h-100">
            <div className="p-4 d-flex flex-column h-100">
                <div className="d-flex align-items-start justify-content-between mb-2">
                    <span className="dash-label">{label}</span>
                    {this.chip(icon, color, tint)}
                </div>
                <div className="d-flex align-items-baseline gap-2">
                    <span className="dash-value">{money(value)}</span>
                    {!!unit && <span className="text-12 text-secondary">{unit}</span>}
                </div>
                {!!foot && <div className="mt-auto pt-3">{foot}</div>}
            </div>
        </div>
    );

    // การ์ดที่แจกแจงเป็นสัดส่วน (สถานะขาย / ประเภทประกาศ ฯลฯ)
    breakdown = (title, map, tones, total) => {
        const entries = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);

        return (
            <div className="prop-card prop-in h-100">
                <div className="prop-card__head">
                    <span className="prop-title text-14">
                        <span className="prop-title__dot" />
                        {title}
                    </span>
                </div>
                <div className="p-3">
                    {!entries.length ? (
                        <div className="dash-empty">ยังไม่มีข้อมูล</div>
                    ) : (
                        <>
                            <div className="dash-segbar mb-3">
                                {entries.map(([key, value]) => (
                                    <span
                                        key={key}
                                        title={`${key} ${money(value)}`}
                                        style={{
                                            width: `${share(value, total)}%`,
                                            background: tones[key] || "#ced4da",
                                            minWidth: value ? 5 : 0,
                                        }}
                                    />
                                ))}
                            </div>
                            {entries.map(([key, value]) => (
                                <div key={key} className="d-flex align-items-center justify-content-between py-1">
                                    <span className="d-flex align-items-center gap-2 text-12">
                                        <span
                                            className="rounded-circle"
                                            style={{ width: 8, height: 8, background: tones[key] || "#ced4da" }}
                                        />
                                        {key}
                                    </span>
                                    <span className="text-12">
                                        <b>{money(value)}</b>
                                        <span className="text-secondary ms-1">{share(value, total)}%</span>
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ---------- กราฟคำขอ 7 วัน ----------
    renderTrend = () => {
        const daily = this.state.data.inquiry.daily || [];
        const max = Math.max(1, ...daily.map((d) => d.total));

        return (
            <div className="prop-card prop-in h-100">
                <div className="prop-card__head">
                    <span className="prop-title text-14">
                        <span className="prop-title__dot" />
                        คำขอนัดดูห้อง 7 วันล่าสุด
                    </span>
                    <span className="text-12 text-secondary">
                        รวม {money(daily.reduce((sum, d) => sum + d.total, 0))} รายการ
                    </span>
                </div>
                <div className="p-3">
                    <div className="dash-bars">
                        {daily.map((d) => (
                            <div className="dash-bar" key={d.date} title={`${dayMonth(d.date)} · ${d.total} รายการ`}>
                                <span className="text-12 fw-bold" style={{ color: d.total ? "#146c43" : "#adb5bd" }}>
                                    {d.total}
                                </span>
                                <div className="dash-bar__fill" style={{ height: `${(d.total / max) * 100}%` }} />
                                <span className="dash-bar__label">{dayMonth(d.date)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // ---------- ห้องที่คนดูมากสุด ----------
    renderTopViewed = () => {
        const rows = this.state.data.top_viewed || [];

        return (
            <div className="prop-card prop-in h-100">
                <div className="prop-card__head">
                    <span className="prop-title text-14">
                        <span className="prop-title__dot" />
                        ห้องที่มีคนดูมากที่สุด
                    </span>
                </div>
                <div className="p-3">
                    {!rows.length ? (
                        <div className="dash-empty">ยังไม่มีใครเข้าดูห้องบนหน้าเว็บ</div>
                    ) : (
                        rows.map((row, index) => (
                            <div className="dash-rank" key={row.uid}>
                                <span className="dash-rank__no">{index + 1}</span>
                                <div className="flex-grow-1 min-w-0">
                                    <div className="text-truncate">ห้อง {row.address_number}</div>
                                    <div className="text-12 text-secondary">
                                        {row.listing_type}
                                        {row.price ? ` · ${shortMoney(row.price)}` : ""}
                                        {row.rent_price ? ` · เช่า ${money(row.rent_price)}/ด.` : ""}
                                    </div>
                                </div>
                                <span className="text-12 fw-bold">{money(row.view_count)} ครั้ง</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    // ---------- คำขอล่าสุด ----------
    renderRecent = () => {
        const rows = this.state.data.recent_inquiries || [];

        return (
            <div className="prop-card prop-in h-100">
                <div className="prop-card__head">
                    <span className="prop-title text-14">
                        <span className="prop-title__dot" />
                        คำขอล่าสุด
                    </span>
                    <a className="prop-link" href="/sale-inquiries">ดูทั้งหมด</a>
                </div>
                <div className="p-3">
                    {!rows.length ? (
                        <div className="dash-empty">ยังไม่มีคำขอเข้ามา</div>
                    ) : (
                        rows.map((row) => (
                            <div className="dash-rank" key={row.uid}>
                                <div className="flex-grow-1 min-w-0">
                                    <div className="text-truncate">
                                        {row.full_name}
                                        <span className="text-secondary text-12 ms-2">{row.phone}</span>
                                    </div>
                                    <div className="text-12 text-secondary">
                                        {row.address_number ? `ห้อง ${row.address_number}` : "สอบถามทั่วไป"}
                                        {row.interest_type ? ` · ${row.interest_type}` : ""}
                                    </div>
                                </div>
                                <span
                                    className="px-2 py-1 rounded-pill text-12"
                                    style={INQUIRY_STATUS_STYLE[row.status] || { background: "#eef1f5", color: "#6c757d" }}
                                >
                                    {row.status}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { data, loaded } = this.state;
        const p = data.portfolio;
        const inq = data.inquiry;
        const newInquiry = inq.status?.[INQUIRY_STATUS.NEW] || 0;
        const project = getCurrentProject();

        return (
            <div className="w-100 min-vh-100 prop-page">
                <PropertyNavbar menuKey="sales-dashboard" page1="ภาพรวม" page2="แดชบอร์ด" projectName={project?.project_name} />

                <div className="body p-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-3 prop-in">
                        <div>
                            <h3 className="fw-bold mb-1 text-dark">แดชบอร์ดงานขาย</h3>
                            <div className="text-secondary text-14">
                                {stripJuristic(project?.project_name) || "-"} · ตัวเลข ณ ตอนนี้
                            </div>
                        </div>
                        {/* งานที่ต้องรีบทำ ต้องเห็นตั้งแต่บนหัวหน้า */}
                        {!!newInquiry && (
                            <a className="prop-btn" style={{ background: "#dc3545", boxShadow: "none" }} href="/sale-inquiries">
                                <span className="icon text-14">{"\uf0f3"}</span>
                                มีคำขอใหม่ {newInquiry} รายการ
                            </a>
                        )}
                    </div>

                    {/* ---------------- ภาพรวมทรัพย์ ---------------- */}
                    {this.section("ภาพรวมทรัพย์", "ณ ตอนนี้")}
                    <div className="row g-3">
                        <div className="col-xl col-lg-4 col-md-6">
                            {this.tile({
                                label: "ห้องทั้งหมดในโครงการ",
                                value: p.room_total,
                                unit: "ห้อง",
                                icon: "\uf015",
                                foot: (
                                    <>
                                        <div className="dash-track">
                                            <div className="dash-fill" style={{ width: `${share(p.listing_total, p.room_total)}%` }} />
                                        </div>
                                        <div className="text-10 text-secondary mt-2">
                                            ลงประกาศแล้ว {money(p.listing_total)} · เหลือ {money(p.not_listed)}
                                        </div>
                                    </>
                                ),
                            })}
                        </div>
                        <div className="col-xl col-lg-4 col-md-6">
                            {/* ★ ไอคอนเดิมเป็นสตริงว่าง (ชิปไอคอนโล่ง) → ใช้โทรโข่ง \uf0a1 แบบเดียวกับไอคอนประกาศใน Navbar */}
                            {this.tile({ label: "ลงประกาศแล้ว", value: p.listing_total, unit: "รายการ", icon: "\uf0a1" })}
                        </div>
                        <div className="col-xl col-lg-4 col-md-6">
                            {this.tile({
                                label: "เผยแพร่บนหน้าเว็บ",
                                value: p.published,
                                unit: "รายการ",
                                icon: "\uf0ac",
                                color: "#2f80ed",
                                tint: "#e7f1ff",
                                // ★ p.hidden = ลงประกาศแล้วแต่ยังไม่ขึ้นเว็บ (ไม่ใช่ห้องที่ยังไม่ลงประกาศ — อันนั้นคือ not_listed)
                                foot: <div className="text-10 text-secondary">{PUBLISH_STATUS.HIDDEN} {money(p.hidden)} รายการ</div>,
                            })}
                        </div>
                        <div className="col-xl col-lg-6 col-md-6">
                            {this.tile({
                                label: "ขายพร้อมผู้เช่า",
                                value: p.sell_with_tenant,
                                unit: "รายการ",
                                icon: "\uf084",
                                color: "#8a5a00",
                                tint: "#fff2d9",
                            })}
                        </div>
                        <div className="col-xl col-lg-6 col-md-6">
                            {this.tile({
                                label: "ยอดเข้าชมรวม",
                                value: data.price.views,
                                unit: "ครั้ง",
                                icon: "\uf06e",
                                color: "#6c757d",
                                tint: "#eef1f5",
                            })}
                        </div>
                    </div>

                    {/* ---------------- แจกแจง ---------------- */}
                    {this.section("แจกแจงประกาศ")}
                    <div className="row g-3">
                        <div className="col-xl-3 col-lg-6">
                            {this.breakdown("ประเภทประกาศ", data.listing_type, {
                                [LISTING_TYPE.SALE]: "#198754",
                                [LISTING_TYPE.RENT]: "#2f80ed",
                                [LISTING_TYPE.BOTH]: "#f0921e",
                            }, p.listing_total)}
                        </div>
                        <div className="col-xl-3 col-lg-6">
                            {this.breakdown("สถานะการขาย", data.selling_status, TONE, p.listing_total)}
                        </div>
                        <div className="col-xl-3 col-lg-6">
                            {this.breakdown("สถานะการเช่า", data.rent_status, TONE, p.listing_total)}
                        </div>
                        <div className="col-xl-3 col-lg-6">
                            <div className="prop-card prop-in h-100">
                                <div className="prop-card__head">
                                    <span className="prop-title text-14">
                                        <span className="prop-title__dot" />
                                        ราคาขาย
                                    </span>
                                </div>
                                <div className="p-3">
                                    {/* ★ นับเฉพาะประกาศที่ใส่ราคาแล้ว ถ้ารวมราคา 0 ค่าเฉลี่ยจะเพี้ยน */}
                                    {!data.price.avg ? (
                                        <div className="dash-empty">ยังไม่มีประกาศที่ระบุราคา</div>
                                    ) : (
                                        [
                                            ["เฉลี่ย", data.price.avg],
                                            ["ต่ำสุด", data.price.min],
                                            ["สูงสุด", data.price.max],
                                        ].map(([label, value]) => (
                                            <div key={label} className="d-flex align-items-center justify-content-between py-2">
                                                <span className="text-12 text-secondary">{label}</span>
                                                <b>{shortMoney(value)}</b>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ---------------- ลูกค้า ---------------- */}
                    {this.section("ลูกค้าและนัดหมาย")}
                    <div className="row g-3">
                        <div className="col-xl-3 col-lg-6">
                            {this.tile({
                                label: "คำขอนัดดูห้องทั้งหมด",
                                value: inq.total,
                                unit: "รายการ",
                                icon: "\uf073",
                                foot: (
                                    <div className="text-10 text-secondary">
                                        นัดวันนี้ {money(inq.visit_today)} รายการ
                                    </div>
                                ),
                            })}
                        </div>
                        <div className="col-xl-3 col-lg-6">
                            {this.breakdown("สถานะคำขอ", inq.status, INQUIRY_TONE, inq.total)}
                        </div>
                        <div className="col-xl-6 col-lg-12">{this.renderTrend()}</div>
                    </div>

                    <div className="row g-3 mt-0 pb-4">
                        <div className="col-xl-6 col-lg-12">{this.renderTopViewed()}</div>
                        <div className="col-xl-6 col-lg-12">{this.renderRecent()}</div>
                    </div>

                    {!loaded && <div className="text-center text-secondary py-4">กำลังโหลดข้อมูล...</div>}
                </div>
            </div>
        );
    }
}
